import React, { useEffect, useState } from "react";
import {
  DatePicker,
  Select,
  Button,
  Table,
  Space,
  Card,
  message,
  Row,
  Col,
  Form,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import ReportService from "../services/reports.service";
import { useStore } from "../context/StoreContext";
import { ReportSummary } from "../entities/interfaces/report";
import { saveAsFile } from "../utils/helpers";
import toast from "react-hot-toast";

const { RangePicker } = DatePicker;
const { Option } = Select;

interface ReportRow {
  invoiceId: string;
  invoiceDate: string;
  service: string;
  quantity: number;
  itemPrice: number;
  itemTax: number;
  itemNet: number;
  customerName: string;
  customerPhone: string;
  staff: string;
  paymentMode: string;
}

interface FilterOption {
  id: string;
  name: string;
}

interface FiltersData {
  customers: FilterOption[];
  staffs: FilterOption[];
  services: FilterOption[];
  paymentmodes: string[];
}

interface FiltersFormValues {
  dateRange: [Dayjs, Dayjs];
  paymentMode?: string[];
  gender?: string;
  staffs?: string[];
  customers?: string[];
  services?: string[];
}

export default function ComprehensiveReportPage() {
  const { selectedStore } = useStore();
  const [filtersData, setFiltersData] = useState<FiltersData>();
  const [reportData, setReportData] = useState<ReportRow[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<FiltersFormValues>();

  const columns: ColumnsType<ReportRow> = [
    {
      title: "S.No",
      key: "index",
      width: 70,
      render: (_text, _record, index) => index + 1,
    },
    { title: "Invoice ID", dataIndex: "invoiceId", key: "invoiceId" },
    {
      title: "Date",
      dataIndex: "invoiceDate",
      render: (d) => dayjs(d).format("YYYY-MM-DD hh:mm:A"),
    },
    { title: "Service", dataIndex: "service", key: "service" },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    { title: "Item Price", dataIndex: "itemPrice", key: "itemPrice" },
    { title: "Item Tax", dataIndex: "itemTax", key: "itemTax" },
    { title: "Item Net", dataIndex: "itemNet", key: "itemNet" },
    { title: "Customer", dataIndex: "customerName", key: "customerName" },
    { title: "Phone", dataIndex: "customerPhone", key: "customerPhone" },
    { title: "Staff", dataIndex: "staff", key: "staff" },
    { title: "Payment Mode", dataIndex: "paymentMode", key: "paymentMode" },
  ];

  // --- ✅ Fetch Report Function ---
  const fetchReport = async (applyFilters = false) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const [from, to] = values.dateRange || [dayjs(), dayjs()];

      const body: any = {};
      if (applyFilters) {
        if (values.paymentMode?.length) body.paymentMode = values.paymentMode;
        if (values.gender) body.gender = values.gender;
        if (values.staffs?.length) body.staffs = values.staffs;
        if (values.customers?.length) body.customers = values.customers;
        if (values.services?.length) body.services = values.services;
      }

      const { data } = await ReportService.getComprehensiveReport(
        from.startOf("date").toISOString(),
        to.endOf("date").toISOString(),
        body
      );

      setReportData(data.report);
      setReportSummary(data.summary);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  };

  // --- ✅ Fetch Filters (only when date range changes) ---
  const fetchFilters = async (from: dayjs.Dayjs, to: dayjs.Dayjs) => {
    try {
      const { data } = await ReportService.getComprehensiveReportFilters(
        from.startOf("date").toISOString(),
        to.endOf("date").toISOString()
      );
      setFiltersData(data);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch filter data");
    }
  };

  // --- Initial Fetch ---
  useEffect(() => {
    form.setFieldsValue({
      dateRange: [dayjs().startOf("date"), dayjs().endOf("date")],
      paymentMode: [],
      gender: undefined,
      staffs: [],
      customers: [],
      services: [],
    });
    fetchReport();
  }, []);

  const exportToPDF = () => {
    if (!reportData?.length) {
      message.warning("No data to export");
      return;
    }

    const doc = new jsPDF("p", "pt", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // --- 📄 PAGE 1: Report Info ---
    doc.setFontSize(18);
    doc.text("Comprehensive Report", pageWidth / 2, 60, { align: "center" });

    doc.setFontSize(12);
    const values = form.getFieldsValue();

    const [from, to] = values.dateRange || [];
    const filtersSummary = [
      ["From", from ? dayjs(from).format("DD MMM YYYY") : "-"],
      ["To", to ? dayjs(to).format("DD MMM YYYY") : "-"],
      ["Gender", values.gender || "All"],
      [
        "Payment Modes",
        values.paymentMode?.length ? values.paymentMode.join(", ") : "All",
      ],
      [
        "Staffs",
        values.staffs?.length
          ? values.staffs
              .map(
                (id: string) =>
                  filtersData?.staffs.find((s) => s.id === id)?.name
              )
              .filter(Boolean)
              .join(", ")
          : "All",
      ],
      [
        "Customers",
        values.customers?.length
          ? values.customers
              .map(
                (id: string) =>
                  filtersData?.customers.find((c) => c.id === id)?.name
              )
              .filter(Boolean)
              .join(", ")
          : "All",
      ],
      [
        "Services",
        values.services?.length
          ? values.services
              .map(
                (id: string) =>
                  filtersData?.services.find((s) => s.id === id)?.name
              )
              .filter(Boolean)
              .join(", ")
          : "All",
      ],
    ];

    autoTable(doc, {
      startY: 90,
      head: [["Filter", "Value"]],
      body: filtersSummary,
      theme: "grid",
      styles: { fontSize: 10 },
    });

    // --- ➕ Add new page for report table ---
    doc.addPage();

    // --- 📊 Report Table ---
    const tableColumnHeaders = [
      "Invoice ID",
      "Date",
      "Service",
      "Qty",
      "Item Price",
      "Item Tax",
      "Item Net",
      "Customer",
      "Phone",
      "Staff",
      "Payment Mode",
    ];

    const tableRows = reportData.map((row) => [
      row.invoiceId,
      dayjs(row.invoiceDate).format("DD MMM YYYY hh:mm A"),
      row.service,
      row.quantity,
      row.itemPrice,
      row.itemTax,
      row.itemNet,
      row.customerName,
      row.customerPhone,
      row.staff,
      row.paymentMode,
    ]);

    autoTable(doc, {
      head: [tableColumnHeaders],
      body: tableRows,
      startY: 50,
      theme: "striped",
      styles: {
        fontSize: 9,
        cellPadding: {
          top: 6,
          bottom: 6,
          left: 3,
          right: 3,
        },
        lineWidth: 0.1, // border thickness
        lineColor: [200, 200, 200], // black border
      },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 90 },
        3: { cellWidth: 25 },
        7: { cellWidth: 60 },
        8: { cellWidth: 60 },
        9: { cellWidth: 50 },
        10: { cellWidth: 40 },
      },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(
          `Page ${(doc as any).internal.getNumberOfPages()}`,
          pageWidth - 60,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    // Add summary row at bottom of table
    if (reportSummary) {
      autoTable(doc, {
        startY: (doc as any).lastAutoTable?.finalY + 5,
        body: [
          [
            "TOTAL",
            reportSummary.totalRecords,
            "",
            reportSummary.totalQuantity,
            reportSummary.totalItemPrice.toFixed(2),
            reportSummary.totalItemTax.toFixed(2),
            reportSummary.totalItemNet.toFixed(2),
            "",
            "",
            "",
            "",
          ],
        ],
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: {
            top: 6,
            bottom: 6,
            left: 3,
            right: 3,
          },
          lineWidth: 0.1, // border thickness
          lineColor: [200, 200, 200], // black border
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 50 },
          2: { cellWidth: 90 },
          3: { cellWidth: 25 },
          7: { cellWidth: 60 },
          8: { cellWidth: 60 },
          9: { cellWidth: 50 },
          10: { cellWidth: 40 },
        },
      });
    }

    // --- 💾 Save file ---
    doc.save(
      `Comprehensive Report - ${selectedStore?.name} - ${from.format(
        "DD/MM/YYYY"
      )} - ${to.format("DD/MM/YYYY")}.pdf`
    );
  };

  const downloadComprehensiveReport = async () => {
    try {
      const values = form.getFieldsValue();
      const [from, to] = values.dateRange;
      if (selectedStore) {
        const res = await ReportService.downloadComprehensiveReport(
          from.startOf("date").toISOString(),
          to.endOf("date").toISOString(),
          values,
          selectedStore.id
        );
        saveAsFile(
          res,
          `Comprehensive Report - ${selectedStore.name} - ${from.format(
            "DD/MM/YYYY"
          )} - ${to.format("DD/MM/YYYY")}.csv`,
          "text/csv;charset=utf-8"
        );
      }
    } catch (err) {
      toast.error("Error downloading report");
    }
  };

  return (
    <div>
      <div className="flex justify-between mb-3">
        <h1 className="text-xl font-bold">Comprehensive Report</h1>
        {selectedStore ? (
          <p>
            Showing reports for the store:{" "}
            <strong>{selectedStore?.name}</strong>
          </p>
        ) : (
          <p>Showing reports for all the stores</p>
        )}
      </div>
      <Card style={{ marginBottom: 16 }} bodyStyle={{ padding: "0.5rem" }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            dateRange: [dayjs(), dayjs()],
            paymentMode: [],
            gender: undefined,
            staffs: [],
            customers: [],
            services: [],
          }}
          onValuesChange={(changedValues, allValues) => {
            if (changedValues.dateRange) {
              const [from, to] = changedValues.dateRange;
              if (from && to) {
                fetchFilters(from, to);
              }
            }
          }}
        >
          <Row gutter={[12, 12]}>
            {/* Row 1 */}
            <Col span={8}>
              <Form.Item
                label="Date Range"
                name="dateRange"
                rules={[
                  { required: true, message: "Please select date range" },
                ]}
                style={{ marginBottom: 8 }}
              >
                <RangePicker allowClear={false} style={{ width: "100%" }} />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Gender"
                name="gender"
                style={{ marginBottom: 8 }}
              >
                <Select placeholder="Select Gender" allowClear>
                  <Option value="MALE">Male</Option>
                  <Option value="FEMALE">Female</Option>
                  <Option value="OTHER">Other</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Payment Mode"
                name="paymentMode"
                style={{ marginBottom: 8 }}
              >
                <Select
                  mode="multiple"
                  placeholder="Select Payment Modes"
                  options={
                    filtersData?.paymentmodes?.map((m) => ({
                      label: m,
                      value: m,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>

            {/* Row 2 */}
            <Col span={8}>
              <Form.Item
                label="Staffs"
                name="staffs"
                style={{ marginBottom: 8 }}
              >
                <Select
                  mode="multiple"
                  placeholder="Select Staffs"
                  options={
                    filtersData?.staffs?.map((s) => ({
                      label: s.name,
                      value: s.id,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Customers"
                name="customers"
                style={{ marginBottom: 8 }}
              >
                <Select
                  mode="multiple"
                  placeholder="Select Customers"
                  options={
                    filtersData?.customers?.map((c) => ({
                      label: c.name,
                      value: c.id,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Services"
                name="services"
                style={{ marginBottom: 8 }}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select Services"
                  options={
                    filtersData?.services?.map((s) => ({
                      label: s.name,
                      value: s.id,
                    })) || []
                  }
                />
              </Form.Item>
            </Col>

            {/* Buttons */}
            <Col span={24} style={{ textAlign: "right" }}>
              <Space className="flex justify-between">
                <Space>
                  <Button onClick={exportToPDF} icon={<DownloadOutlined />}>
                    Export PDF
                  </Button>
                  <Button onClick={downloadComprehensiveReport}>
                    Export CSV
                  </Button>
                </Space>
                <Space>
                  <Button
                    type="primary"
                    onClick={() => fetchReport(true)}
                    loading={loading}
                  >
                    Apply Filters
                  </Button>
                  <Button
                    onClick={() => {
                      form.resetFields();
                      form.setFieldsValue({
                        dateRange: [dayjs(), dayjs()],
                      });
                      fetchReport();
                    }}
                  >
                    Reset
                  </Button>
                </Space>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card bodyStyle={{ padding: "0.5rem" }}>
        <Table
          columns={columns}
          dataSource={reportData}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          scroll={{ x: "max-content" }}
          summary={(pageData) => {
            if (!reportSummary) return null;
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={6}></Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    {reportSummary.totalRecords}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    {reportSummary.totalQuantity}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    {reportSummary.totalItemPrice.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    {reportSummary.totalItemTax.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    {reportSummary.totalItemNet.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}></Table.Summary.Cell>
                  <Table.Summary.Cell index={8}></Table.Summary.Cell>
                  <Table.Summary.Cell index={9}></Table.Summary.Cell>
                  <Table.Summary.Cell index={10}></Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </div>
  );
}
